import { useStoreState } from "ezStore";
import { FormGroup } from "@cofano/cck";
import { DataFormat, Fields, hasOptionalFieldPermission, Language } from "utils";
import { productVolumeCorrectionConversionMethodField } from "view/products/components/form/fields/ProductVolumeCorrectionConversionMethod";
import { Product } from "view/products/utils/product.types";

const ProductGeneral = ({ product }: { product: Product }) => {

	const session = useStoreState(store => store.session);

	const getRangedValue = (name: keyof Product, nameMin: keyof Product, nameMax: keyof Product, addonAfter?: string) => {
		const addonAfterString = addonAfter ? addonAfter : "";
		if (product[name]) {
			return DataFormat.simpleUnit(Number(product[name]), addonAfterString);
		} else if (product[nameMin] && product[nameMax]) {
			return `${DataFormat.simpleUnit(Number(product[nameMin]), addonAfterString)} - ${DataFormat.simpleUnit(Number(product[nameMax]), addonAfterString)}`;
		} else if (product[nameMin]) {
			return `> ${DataFormat.simpleUnit(Number(product[nameMin]), addonAfterString)}`;
		} else if (product[nameMax]) {
			return `< ${DataFormat.simpleUnit(Number(product[nameMax]), addonAfterString)}`;
		} else {
			return "-";
		}
	};

	return (
		<div>
			{hasOptionalFieldPermission(session, Fields.PRODUCT, "loadingType") && (
				<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_LOADINGTYPE")}>
					{product.loadingType ? Language(`PRODUCTS_${product.loadingType}`) : "-"}
				</FormGroup>
			)}

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_NAME")}>
				{product.name || "-"}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_PREVIOUS_CARGO_DISPLAY_NAME")}>
				{product.previousCargoDisplayName || "-"}
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.PRODUCT, "code") && (
				<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_CODE")}>
					{product.code || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.PRODUCT, "externalId") && (
				<FormGroup overflow="visible" labelPlacement="left-center" label={Language("EXTERNAL_ID")}>
					{product.externalId || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.PRODUCT, "abbreviation") && (
				<FormGroup overflow="visible" labelPlacement="left-center" label={Language("ABBREVIATION")}>
					{product.abbreviation || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.PRODUCT, "category") && (
				<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCT_CATEGORY")}>
					{product.category?.name || "-"}
				</FormGroup>
			)}

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_CAS_NUMBER")}>
				{product.casNumber || "-"}
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.PRODUCT, "description") && (
				<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_DESCRIPTION")}>
					{product.description || "-"}
				</FormGroup>
			)}

			{!product.isDangerousGood && (
				<>
					<FormGroup overflow="visible" label={Language("PRODUCT_ADN_PROPER_SHIPPING_NAME_EN")} labelPlacement="left-center">
						{product.properShippingNameEN || "-"}
					</FormGroup>

					<FormGroup overflow="visible" label={Language("PRODUCT_ADN_PROPER_SHIPPING_NAME_NL")} labelPlacement="left-center">
						{product.properShippingNameNL || "-"}
					</FormGroup>

					<FormGroup overflow="visible" label={Language("PRODUCT_ADN_PROPER_SHIPPING_NAME_DE")} labelPlacement="left-center">
						{product.properShippingNameDE || "-"}
					</FormGroup>

					<FormGroup overflow="visible" label={Language("PRODUCT_ADN_PROPER_SHIPPING_NAME_FR")} labelPlacement="left-center">
						{product.properShippingNameFR || "-"}
					</FormGroup>
				</>
			)}

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("NOTES")}>
				<span style={{ whiteSpace: "pre-wrap" }}>{product.remark || "-"}</span>
			</FormGroup>

			{!product.isDangerousGood && (
				<>
					<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_DENSITY")}>
						{getRangedValue("densityKgM3", "densityKgM3Min", "densityKgM3Max", DataFormat.UNITS.DENSITY)}
					</FormGroup>

					<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCT_ADN_DENSITY_TEMPERATURE")}>
						{product.densityTemperature ? DataFormat.simpleUnit(product.densityTemperature, "°C") : "-"}
					</FormGroup>
				</>
			)}

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_VOLUME_CORRECTION_CONVERSION_METHOD")}>
				{productVolumeCorrectionConversionMethodField(product.volumeCorrectionConversionMethod)}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_DENSITY_CORRECTION_FACTOR")}>
				{product.densityCorrectionFactor ? DataFormat.simpleUnit(product.densityCorrectionFactor, "") : "-"}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_VAPOR_PRESSURE")}>
				{getRangedValue("vaporPressure", "vaporPressureMin", "vaporPressureMax", "kPa")}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_VAPOR_PRESSURE_TEMPERATURE")}>
				{product.vaporPressureTemperature ? DataFormat.temperature(product.vaporPressureTemperature) : "-"}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_VISCOSITY")}>
				{getRangedValue("viscosity", "viscosityMin", "viscosityMax", "mm²/s")}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_FLASH_POINT")}>
				{getRangedValue("flashPoint", "flashPointMin", "flashPointMax", "°C")}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_MELTING_POINT")}>
				{getRangedValue("meltingPoint", "meltingPointMin", "meltingPointMax", "°C")}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_BOILING_POINT")}>
				{getRangedValue("boilingPoint", "boilingPointMin", "boilingPointMax", "°C")}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_AUTO_IGNITION_TEMPERATURE")}>
				{getRangedValue("autoIgnitionTemperature", "autoIgnitionTemperatureMin", "autoIgnitionTemperatureMax", "°C")}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_SELF_ACCELERATING_DECOMPOSITION_POLYMERISATION_TEMPERATURE")}>
				{product.selfAcceleratingDecompositionPolymerisationTemperature ? DataFormat.temperature(product.selfAcceleratingDecompositionPolymerisationTemperature) : "-"}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_MAXIMUM_CONTACT_TEMPERATURE")}>
				{product.maximumContactTemperature ? DataFormat.temperature(product.maximumContactTemperature) : "-"}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_LOWER_EXPLOSIVE_LIMIT")}>
				{product.lowerExplosiveLimit ? DataFormat.percentage(product.lowerExplosiveLimit) : "-"}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_UPPER_EXPLOSIVE_LIMIT")}>
				{product.upperExplosiveLimit ? DataFormat.percentage(product.upperExplosiveLimit) : "-"}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_ACCEPTED_VENT_FREE_LEVEL")}>
				{product.acceptedVentFreeLevel ? DataFormat.percentage(product.acceptedVentFreeLevel) : "-"}
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_PRODUCT_INSTRUCTIONS_FOR_VESSEL")}>
				<span style={{ whiteSpace: "pre-wrap" }}>{product.productInstructionsForVessel || "-"}</span>
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_HEATING_INSTRUCTIONS_FOR_VESSEL")}>
				<span style={{ whiteSpace: "pre-wrap" }}>{product.heatingInstructionsForVessel || "-"}</span>
			</FormGroup>
		</div>
	);
};

export default ProductGeneral;
